import { PhoneNumber } from "antd-phone-input";
import dayjs from "dayjs";

import { NetworkType } from "interfaces";
import moment from "moment";
import { isDefined, isValidDateRange, WALLET_NETWORK } from "utils";
import { plural } from "utils/textUtils";

const createValidate = (validation: (value: any) => boolean, defaultError?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (value && validation(value)) {
        return Promise.reject(new Error(defaultError || "Это поле является обязательным"));
      }
      return Promise.resolve();
    },
  });
};

export const _isRequired = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (value === 0) return Promise.resolve();
      if (!value || String(value).trim() === "") {
        return Promise.reject(new Error(error || "Это поле является обязательным"));
      }
      return Promise.resolve();
    },
  });
};

export const _dateIsNotFuture = (error?: string) => {
  return () => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      if (dayjs(value).isAfter(dayjs().endOf("day"))) {
        return Promise.reject(new Error(error || "Введите дату не позднее сегодня"));
      }

      return Promise.resolve();
    },
  });
};

export const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;
export const trxAddressRegex = /^T[A-Za-z1-9]{33}$/;

export const _isValidNetworkAddress = (networkType: NetworkType, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value || String(value).trim() === "") {
        return Promise.resolve();
      }

      if (networkType === WALLET_NETWORK.ETH) {
        if (!ethAddressRegex.test(value)) {
          return Promise.reject(new Error(error || "Неверный формат ввода"));
        }
      }

      if (networkType === WALLET_NETWORK.TRX) {
        if (!trxAddressRegex.test(value)) {
          return Promise.reject(new Error(error || "Неверный формат ввода"));
        }
      }

      return Promise.resolve();
    },
  });
};

const fullNameRegex = /^[a-zA-Zа-яА-ЯёЁ]+(?:[-' ][a-zA-Zа-яА-ЯёЁ]+)*$/;

export const _validateFullName = (error?: string, options: { minLength?: number; maxLength?: number } = {}) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      const maxLength = options.maxLength ?? 50;
      const minLength = options.minLength ?? 2;

      if (!value || String(value).trim() === "") {
        return Promise.resolve();
      }

      if (value.length > maxLength) {
        return Promise.reject(new Error(error || `Максимальная длина поля ${maxLength} символов`));
      }

      if (value.length < minLength) {
        return Promise.reject(new Error(error || `Минимальная длина поля ${minLength} символа`));
      }

      if (!fullNameRegex.test(value)) {
        return Promise.reject(
          new Error(
            error ||
              "Поле должно содержать только буквы, дефисы (-), апострофы (') и пробелы. Спецсимволы не могут быть первым или последним символом."
          )
        );
      }

      return Promise.resolve();
    },
  });
};

const phoneRegex = /^7\d{10}$/;

export const _isPhoneNumber = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: PhoneNumber | string) {
      const formatErrorMessage = "Введите корректный номер телефона в формате +7 (XXX) XXX-XX-XX";

      if (typeof value === "string") {
        const unmaskedValue = value.replace(/\D/g, "");

        if (unmaskedValue.length !== 11 || !phoneRegex.test(unmaskedValue)) {
          return Promise.reject(new Error(error || formatErrorMessage));
        }

        return Promise.resolve();
      }

      if (typeof value === "object") {
        const { countryCode, areaCode, phoneNumber } = value;
        const fullNumber = `${countryCode || ""}${areaCode || ""}${phoneNumber || ""}`;

        const unmaskedValue = fullNumber.replace(/\D/g, "");

        if (unmaskedValue.length !== 11 || !phoneRegex.test(unmaskedValue)) {
          return Promise.reject(new Error(error || formatErrorMessage));
        }

        return Promise.resolve();
      }

      return Promise.resolve();
    },
  });
};

const lettersRegex = /^[a-zA-ZА-Яа-яЁё\s]*$/;

export const _isOnlyLetters = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!lettersRegex.test(String(value).trim())) {
        return Promise.reject(new Error(error || "Значение может содержать только буквы"));
      }

      return Promise.resolve();
    },
  });
};

export const _isRequiredDate = (error?: string, format?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!isDefined(value) || value === "") {
        return Promise.reject(new Error(error || "Заполните поле"));
      }

      if (moment(value, format).format(format) === "Invalid date") {
        return Promise.reject(new Error("Некорректная дата"));
      }

      return Promise.resolve();
    },
  });
};

export const _isRequiredDateRange = (error?: string, format?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!isDefined(value) || !Array.isArray(value) || value.length !== 2) {
        return Promise.reject(new Error(error || "Заполните обе даты"));
      }

      const [startDate, endDate] = value;

      if (!isDefined(startDate) || !isDefined(endDate)) {
        return Promise.reject(new Error("Обе даты должны быть заполнены"));
      }

      if (!moment(startDate, format, true).isValid() || !moment(endDate, format, true).isValid()) {
        return Promise.reject(new Error("Некорректная дата"));
      }

      if (moment(startDate).isAfter(moment(endDate))) {
        return Promise.reject(new Error("Дата начала не может быть позже даты окончания"));
      }

      return Promise.resolve();
    },
  });
};

export const _endDateNotBeforeToday = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_rule: any, value: any) {
      if (!isDefined(value) || !Array.isArray(value) || value.length !== 2) {
        return Promise.resolve();
      }

      const endDate = value[1];

      if (!isDefined(endDate)) {
        return Promise.resolve();
      }

      if (dayjs(endDate).isBefore(dayjs().startOf("day"))) {
        return Promise.reject(new Error(error || "Конечная дата не может быть раньше сегодняшнего дня"));
      }

      return Promise.resolve();
    },
  });
};

const emailRegExp =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export const _isEmail = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      if (!emailRegExp.test(value)) {
        return Promise.reject(new Error(error || "Некорректный e-mail"));
      }

      const [, domain] = value.split("@");

      if (!domain.includes(".")) {
        return Promise.reject(new Error(error || "Некорректный e-mail"));
      }

      return Promise.resolve();
    },
  });
};

export const _lessOrEqual = (limit: number, error?: string) => {
  return () => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");

      if (+normalizedValue > limit) {
        return Promise.reject(new Error(error || `Значение должно быть меньше или равно ${limit}`));
      }
      return Promise.resolve();
    },
  });
};

export const _lessThan = (limit: number, error?: string) => {
  return () => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");

      if (+normalizedValue < limit) {
        return Promise.resolve();
      } else {
        return Promise.reject(new Error(error || `Значение должно быть меньше ${limit}`));
      }
    },
  });
};

export const _greaterOrEqual = (limit: number, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");

      if (+normalizedValue < limit) {
        return Promise.reject(new Error(error || `Значение должно быть больше или равно ${limit}`));
      }
      return Promise.resolve();
    },
  });
};

export const _lessOrEqualToComputed = (
  computeLimit: (getFieldValue: (name: string | string[]) => any) => number,
  error?: string
) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (value === undefined || value === null || String(value).trim() === "") {
        return Promise.resolve();
      }

      const normalize = (raw: any) => {
        const num = Number(
          String(raw ?? "")
            .replace(/\s/g, "")
            .replace(",", ".")
        );
        return isNaN(num) ? 0 : num;
      };

      const numericValue = normalize(value);
      const limit = computeLimit(getFieldValue);

      if (numericValue > limit) {
        return Promise.reject(new Error(error || `Значение должно быть меньше или равно ${limit}`));
      }
      return Promise.resolve();
    },
  });
};

export const _greaterThan = (limit: number, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }
      const normalizedValue = String(value).replace(",", ".");

      if (+normalizedValue < limit && value !== "") {
        return Promise.reject(new Error(error || `Значение должно быть больше ${limit}`));
      }
      return Promise.resolve();
    },
  });
};

export const _isNumericWithPrecision = (precision: number = 3, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");
      const regex = new RegExp(`^\\d{1,${precision}}(\\.\\d{1,2})?$`);

      if (!regex.test(normalizedValue)) {
        return Promise.reject(new Error(error || `Введено невалидное значение`));
      }
      return Promise.resolve();
    },
  });
};

export const _isRate = (error?: string) => {
  return () => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");
      const regex = /^\d{1,4}(\.\d{1,4})?$/;

      if (!regex.test(normalizedValue)) {
        return Promise.reject(new Error(error || "Введено невалидное значение"));
      }
      return Promise.resolve();
    },
  });
};

export const _isNumber = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");
      const regex = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

      if (regex.test(normalizedValue)) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(error || "Введите корректное число"));
    },
  });
};

export const _isTheSameAs = (fieldName: string, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      const checkedFieldValue = getFieldValue(fieldName);

      if (!value || checkedFieldValue === value) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(error || "Поля не совпадают"));
    },
  });
};

export const _isLengthBetween = (minLength: number, maxLength: number) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      let error;

      if (!value) {
        return Promise.resolve();
      }

      if (!!value && (value.length < minLength || value.length > maxLength)) {
        error = `Длина значения должна быть от ${minLength} до ${maxLength} символов`;

        if (minLength === maxLength) {
          error = `Длина значения должна быть ${minLength} символов`;
        }
      }

      if (error) {
        return Promise.reject(new Error(error));
      } else {
        return Promise.resolve();
      }
    },
  });
};

export const _isLengthEqual = (length: number, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value && value !== 0) {
        return Promise.resolve();
      }

      const stringValue = String(value);
      if (stringValue.length !== length) {
        return Promise.reject(new Error(error || `Длина значения должна быть равна ${length}`));
      }

      return Promise.resolve();
    },
  });
};

export const _noForbiddenSymbols = (msg?: string) =>
  createValidate((value) => !/^[A-Za-z\d!.,\-_@#$%^&]+$/gi.test(value), msg);

export const _includesLatin = (msg?: string) => createValidate((value) => !/^.*[A-Za-z].*$/.test(value), msg);

export const _includesSpecialSymbol = (msg?: string) =>
  createValidate((value) => !/^.*[!.,\-_@#$%^&].*$/.test(value), msg);

export const _includesNumbers = (msg?: string) => createValidate((value) => !RegExp("(.*\\d.*)").test(value), msg);

export const _isValidCommentText = (msg?: string) =>
  createValidate((value) => !/^[A-Za-zА-Яа-яЁё0-9\s.,?!]+$/.test(value), msg || "Поле содержит недопустимые символы");

export const _isOnlyNumbersWithLength = (length: number, msg?: string) =>
  createValidate(
    (value) => !/^\d+$/.test(value) || value.length !== length,
    msg ||
      `Значение должно содержать только цифры и быть длиной ${length} ${plural(length, [
        "символа",
        "символов",
        "символов",
      ])}`
  );

export const _isNotLongerThan = (maxLength: number, msg?: string) =>
  createValidate(
    (value) => value.length > maxLength,
    msg || `Длина значения не должна превышать ${maxLength} ${plural(maxLength, ["символа", "символов", "символов"])}`
  );

export const _isInteger = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const num = Number(value);
      if (isNaN(num) || !Number.isInteger(num)) {
        return Promise.reject(new Error(error || "Введите целое число"));
      }

      return Promise.resolve();
    },
  });
};

export const _isIntegerInRange = (min: number, max: number, error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value) {
        return Promise.resolve();
      }

      const normalizedValue = String(value).replace(",", ".");
      const num = Number(normalizedValue);

      if (isNaN(num) || !Number.isInteger(num)) {
        return Promise.reject(new Error(error || "Введите целое число"));
      }

      if (num < min || num >= max) {
        return Promise.reject(new Error(error || `Число должно быть от ${min} до ${max} (не включая ${max})`));
      }

      return Promise.resolve();
    },
  });
};

export const _isRequiredAttorneyWhenDateExists = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      const attorneyDate = getFieldValue("attorneyDate");

      if (isValidDateRange(attorneyDate)) {
        if (!value || String(value).trim() === "") {
          return Promise.reject(new Error(error || "Укажите номер доверенности"));
        }
      }

      return Promise.resolve();
    },
  });
};

export const unkFormatRegex = /^(\d+)\/(\d+)\/(\d+)\/(\d+)\/(\d+)$/;

export const _isValidUnkFormat = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value || String(value).trim() === "") {
        return Promise.resolve();
      }

      const cleanValue = String(value).replace(/\s/g, "");

      if (!unkFormatRegex.test(cleanValue)) {
        return Promise.reject(new Error(error || "Неверный формат УНК"));
      }

      return Promise.resolve();
    },
  });
};

export const _isRegex = ({ regex, error, required = true }: { regex: RegExp; error?: string; required?: boolean }) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (required && (!value || String(value).trim() === "")) {
        return Promise.reject(new Error("Это поле является обязательным"));
      }
      if (!regex.test(value)) {
        return Promise.reject(new Error(error || "Неверный формат"));
      }
      return Promise.resolve();
    },
  });
};

const companyNameRegex = /^[А-Яа-яеЁ0-9 "№.+-]*$/;

export const _isCorrectCompanyName = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value || String(value).trim() === "") {
        return Promise.reject(new Error(error || "Это поле является обязательным"));
      }

      if (companyNameRegex.test(value)) {
        return Promise.resolve();
      }
      if (!companyNameRegex.test(value)) {
        return Promise.reject(
          new Error(error || `Неверный формат названия компании. Только кириллица, цифры и символы: " № . + -`)
        );
      }

      return Promise.resolve();
    },
  });
};

export const _isToggleChecked = (error?: string, checkedValue?: boolean) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (value !== checkedValue) {
        return Promise.reject(new Error(error || "Значение должно быть равно " + checkedValue));
      }
      return Promise.resolve();
    },
  });
};

const addressRegex = /^[1-9А-ЯЁа-яеA-Za-z][0-9А-ЯЁа-яеA-Za-z №N.,()/\"«»-]{0,250}$/;

export const _isCorrectAddress = (error?: string) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!value || String(value).trim() === "") {
        return Promise.reject(new Error(error || "Это поле является обязательным"));
      }

      if (value.length < 1) {
        return Promise.reject(new Error(error || "Длина значения должна быть от 1 до 255 символов"));
      }

      if (addressRegex.test(value)) {
        return Promise.resolve();
      }
      if (!addressRegex.test(value)) {
        return Promise.reject(
          new Error(
            error ||
              `Неверный формат адреса. Допустимы кириллица, латиница, цифры (кроме 0 в начале), пробел и символы № N . , ( ) / " « » —`
          )
        );
      }

      return Promise.resolve();
    },
  });
};

const nameRegex = /^[А-ЯЁA-Z][А-ЯЁа-яеA-Za-z -]{0,49}$/;

export const _isCorrectName = ({ error, required = true }: { error?: string; required?: boolean } = {}) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (!required && (!value || String(value).trim() === "")) {
        return Promise.resolve();
      }
      if (required && (!value || String(value).trim() === "")) {
        return Promise.reject(new Error("Это поле является обязательным"));
      } else if (value.length > 50) {
        return Promise.reject(new Error(error || "Длина значения должна быть от 0 до 50 символов"));
      }

      if (value.length < 1) {
        return Promise.reject(new Error(error || "Длина значения должна быть от 0 до 50 символов"));
      }
      if (!nameRegex.test(value)) {
        return Promise.reject(new Error(error || "Начинается с заглавной, кириллица/латиница"));
      }
      return Promise.resolve();
    },
  });
};

/**
 * Проверяет серию, номер паспорта, код подразделения на правильность формата
 * @param regex - регулярное выражение для проверки
 * @param error - сообщение об ошибке
 * @param required - обязательное ли поле
 * @returns
 */
export const _isPassportCorrectValue = ({
  regex,
  error,
  required = true,
  length,
}: {
  regex?: RegExp;
  error?: string;
  required?: boolean;
  length: number;
}) => {
  return ({ getFieldValue }: any) => ({
    validator(_: any, value: any) {
      if (required && (!value || String(value).trim() === "")) {
        return Promise.reject(new Error("Это поле является обязательным"));
      }
      if (value.length !== length) {
        return Promise.reject(new Error(`Длина значения должна быть равна ${length}`));
      }
      if (regex && !regex.test(value)) {
        return Promise.reject(new Error(error || "Неверный формат"));
      }
      return Promise.resolve();
    },
  });
};

export const _maxDecimals = (max: number) => () => ({
  validator(_: unknown, value: unknown) {
    if (value === undefined || value === null || value === "") {
      return Promise.resolve();
    }
    const normalized = String(value).replace(",", ".");
    const decimals = normalized.split(".")[1]?.length ?? 0;
    if (decimals > max) {
      return Promise.reject(new Error(`Не более ${max} знаков после запятой`));
    }
    return Promise.resolve();
  },
});

export const _discountDoesNotExceedFee = (baseFee: number, error?: string) => () => ({
  validator(_: unknown, value: unknown) {
    if (value === undefined || value === null || String(value).trim() === "") {
      return Promise.resolve();
    }

    const availableFee = Math.round(baseFee * 100) / 100;
    const customFee = Math.round(Number(String(value).replace(",", ".")) * 100) / 100;

    if (availableFee + customFee < 0) {
      return Promise.reject(new Error(error || "Скидка не может превышать ставку комиссии"));
    }

    return Promise.resolve();
  },
});
